import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { User, Target, AlertTriangle, MessageSquare, Tag } from "lucide-react";
import type { PersonaWithPermissions } from "@/lib/persona-types";

interface PersonaViewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  persona: PersonaWithPermissions | null;
}

export function PersonaViewModal({
  open,
  onOpenChange,
  persona,
}: PersonaViewModalProps) {
  if (!persona) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-6 h-6 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-xl">{persona.name}</DialogTitle>
              <p className="text-sm text-muted-foreground">{persona.role}</p>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Tags */}
          {persona.tags.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Tag className="w-4 h-4" />
                Tags
              </div>
              <div className="flex flex-wrap gap-2">
                {persona.tags.map((tag, i) => (
                  <Badge key={i} variant="secondary">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Goals */}
          {persona.goals.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Target className="w-4 h-4" />
                Goals
              </div>
              <ul className="space-y-2">
                {persona.goals.map((goal, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                    {goal}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Constraints */}
          {persona.constraints.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <AlertTriangle className="w-4 h-4" />
                Constraints
              </div>
              <ul className="space-y-2">
                {persona.constraints.map((constraint, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-reqqy-warning mt-2 flex-shrink-0" />
                    {constraint}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Context */}
          {persona.context && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <MessageSquare className="w-4 h-4" />
                Context
              </div>
              <p className="text-sm text-foreground/80 bg-muted/30 p-3 rounded-lg">
                {persona.context}
              </p>
            </div>
          )}

          {/* Metadata */}
          <div className="pt-4 border-t border-border text-xs text-muted-foreground flex justify-between">
            <span>Created: {new Date(persona.created_at).toLocaleDateString()}</span>
            <span>Updated: {new Date(persona.updated_at).toLocaleDateString()}</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
